// src/components/Assets/AssetsList.js
// Complete assets list with CRUD operations

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import { useAssets } from "./hooks/useAssets";
import AssetCard from "./AssetCard";
import AssetForm from "./AssetForm";
import ConfirmationModal from "./ConfirmationModal";
import "./style.css";

const AssetsList = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { assets, loading, addAsset, editAsset, removeAsset } = useAssets();

  const [showForm, setShowForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    asset: null,
    isSubmitting: false
  });

  // Handle edit action
  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setShowForm(true);
  };

  // Handle delete action with confirmation
  const handleDelete = (asset) => {
    setDeleteModal({
      isOpen: true,
      asset,
      isSubmitting: false
    });
  };

  // Execute delete action
  const handleConfirmDelete = async () => {
    setDeleteModal(prev => ({ ...prev, isSubmitting: true }));
    await removeAsset(deleteModal.asset.id);
    setDeleteModal({
      isOpen: false,
      asset: null,
      isSubmitting: false
    });
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    if (!deleteModal.isSubmitting) {
      setDeleteModal({
        isOpen: false,
        asset: null,
        isSubmitting: false
      });
    }
  };

  // Handle add expense action - navigate to expenses tab with asset context
  const handleAddExpense = (asset) => {
    navigate("/assets/expenses", { state: { selectedAsset: asset, openForm: true } });
  };

  // Handle form submission (create or update)
  const handleFormSubmit = async (assetData) => {
    setIsSubmitting(true);

    let success = false;

    if (selectedAsset) {
      // Update existing asset
      success = await editAsset({ ...assetData, id: selectedAsset.id });
    } else {
      // Create new asset
      success = await addAsset(assetData);
    }

    setIsSubmitting(false);

    if (success) {
      setShowForm(false);
      setSelectedAsset(null);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedAsset(null);
  };

  // Handle add new asset button
  const handleAddNew = () => {
    setSelectedAsset(null);
    setShowForm(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="assets-list-container">
        <div className="loading-spinner">
          {t("Loading...")}
        </div>
      </div>
    );
  }

  return (
    <div className="assets-list-container">
      {/* Header with Title and Add Button */}
      <div className="list-header">
        <div className="list-header-content">
          <h2 className="list-title">{t("Assets")}</h2>
          <p className="list-subtitle">{t("Manage your tools, machinery, and equipment")}</p>
        </div>
        <button className="button-primary" onClick={handleAddNew}>
          <FaPlus /> {t("Add New Asset")}
        </button>
      </div>

      {/* Assets Grid or Empty State */}
      {assets.length === 0 ? (
        <div className="empty-state">
          <p>{t("No assets found")}</p>
          <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#999" }}>
            {t("Click 'Add New' to create your first asset")}
          </p>
        </div>
      ) : (
        <div className="assets-grid">
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddExpense={handleAddExpense}
              canEdit={true}
              canDelete={true}
            />
          ))}
        </div>
      )}

      {/* Asset Form Modal */}
      {showForm && (
        <AssetForm
          asset={selectedAsset}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t("Delete Asset")}
        message={
          deleteModal.asset
            ? t("You are about to permanently delete \"{{assetName}}\". This will remove all associated data including expenses, permissions, and usage history. This action cannot be undone.", {
                assetName: deleteModal.asset.name
              })
            : t("Are you sure you want to delete this asset?")
        }
        confirmText={t("Delete")}
        cancelText={t("Cancel")}
        type="danger"
        isSubmitting={deleteModal.isSubmitting}
      />
    </div>
  );
};

export default AssetsList;
